import {
    Divider,
    Grid,
    IconButton,
    Paper,
    styled,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { Observer } from "mobx-react";
import moment from "moment";
import React from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { InteractionType } from "../../../models/InteractionType";
import { useStore } from "../../../utils/useStore";
import { EvalStore } from "../Stores/EvalStore";
import { isTextMediaType } from "./EvalPage";

const StyledLabelContentGrid = styled(Grid)({
    wordBreak: "break-all",
});


interface InteractionMetadataProps {
    drawerPixelOffset: number;
    interactionType?: InteractionType;
}

export default function InteractionMetadata(props: InteractionMetadataProps) {
    const store = useStore(EvalStore);
    return (
        <Observer>
            {() => (
                <Paper
                    square
                    sx={{
                        width: "100%",
                        boxShadow: "-2px 0 2px -2px #888",
                        height: "100%",
                        overflowX: "hidden",
                        paddingTop: "12px",
                        minWidth: "141px",
                    }}
                >
                    <div
                        style={{
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            paddingBottom: "12px",
                            paddingTop: "6px",
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography variant="h2" gutterBottom={false}>
                            Interaction Metadata
                        </Typography>
                        <IconButton
                            onClick={store.toggleShowInteractionMetadata}
                            size="large"
                        >
                            <CloseIcon fontSize={"medium"} />
                        </IconButton>
                    </div>
                    <Divider variant={"fullWidth"} />
                    <div
                        style={{
                            overflowX: "hidden",
                            margin: "12px",
                            height: `calc(100% - ${
                                isTextMediaType(props.interactionType)
                                    ? 0
                                    : props.drawerPixelOffset + 100 // Account for Metadata header
                            }px)`,
                        }}
                    >
                        <Scrollbars autoHide>
                            <Grid container spacing={2}>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Duration
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.callDurationMillis
                                            ? moment
                                                  .utc(
                                                      store.currentEval
                                                          ?.interaction
                                                          ?.audioMetadata
                                                          ?.callDurationMillis,
                                                  )
                                                  .format("mm:ss")
                                            : "Unavailable"}
                                    </Grid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            File Name
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.fileName ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            File Size
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.fileSizeKiloBytes
                                            ? store.currentEval?.interaction
                                                  ?.audioMetadata
                                                  ?.fileSizeKiloBytes + " KB"
                                            : "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Interaction Date
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction?.InteractionDateAsMoment?.format(
                                            "MM/DD/YYYY, h:mm:ss a",
                                        ) || "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Arrived Date
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {moment(
                                            store.currentEval?.interaction
                                                ?.audioMetadata?.arrivedOn,
                                        ).format("MM/DD/YYYY") || "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Sampled Date
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {moment(
                                            store.currentEval?.createdOn,
                                        ).format("MM/DD/YYYY") || "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Client Call Id
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.clientCallId ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Metadata 1
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.meta1 ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Metadata 2
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.meta2 ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Metadata 3
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.meta3 ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Metadata 4
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.meta4 ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                <Grid item container alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="h2">
                                            Metadata 5
                                        </Typography>
                                    </Grid>
                                    <StyledLabelContentGrid item xs={12}>
                                        {store.currentEval?.interaction
                                            ?.audioMetadata?.meta5 ||
                                            "Unavailable"}
                                    </StyledLabelContentGrid>
                                </Grid>
                                {store.getTaskLoading(
                                    "Get Extended Metadata",
                                ) ? (
                                    <AcxLoadingIndicator
                                        size={24}
                                        alternate="PuffLoader"
                                    />
                                ) : store.extendedMetadataValues ? (
                                    Object.entries(
                                        store.extendedMetadataValues,
                                    ).map((kv_pair) => {
                                        return (
                                            <Grid
                                                item
                                                container
                                                alignItems="center"
                                                xs={12}
                                            >
                                                <Grid item xs={12}>
                                                    <Typography variant="h2">
                                                        {kv_pair[0]}
                                                    </Typography>
                                                </Grid>
                                                <StyledLabelContentGrid
                                                    item
                                                    xs={12}
                                                >
                                                    {!!kv_pair[1] &&
                                                        (kv_pair[1] as any)}
                                                </StyledLabelContentGrid>
                                            </Grid>
                                        );
                                    })
                                ) : (
                                    <Grid
                                        item
                                        container
                                        alignItems="center"
                                        xs={12}
                                    >
                                        <Grid item xs={12}>
                                            <Typography variant="h2">
                                                Extended Metadata
                                            </Typography>
                                        </Grid>
                                        <StyledLabelContentGrid
                                            item
                                            xs={12}
                                        >
                                            Unavailable
                                        </StyledLabelContentGrid>
                                    </Grid>
                                )}
                            </Grid>
                        </Scrollbars>
                    </div>
                </Paper>
            )}
        </Observer>
    );
}
